import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "./useCurrentOrgId";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

interface UserProfile {
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  sonax_widget_script: string | null;
  id_sonax: string | null;
  senha_sonax: string | null;
  role: 'admin' | 'rep' | 'manager' | 'owner';
  isAdmin: boolean;
  isOwner: boolean;
  created_at?: string;
}

// Cache compartilhado para evitar múltiplas chamadas
const userCache = new Map<string, { user: UserProfile | null; timestamp: number; promise?: Promise<UserProfile | null> }>();
const CACHE_TTL = 30000; // 30 segundos

// DEV: evita N logs idênticos (uma instância do hook por componente) no cache hit
let devLastLoggedCacheHitOrgId: string | null = null;

function devLogCachedUserOnce(orgId: string, email: string | null) {
  if (!import.meta.env.DEV) return;
  if (devLastLoggedCacheHitOrgId === orgId) return;
  devLastLoggedCacheHitOrgId = orgId;
  console.log("🟣 [useCurrentUser] Using cached user", { orgId, email });
}

// Função para limpar cache expirado
const cleanExpiredCache = () => {
  const now = Date.now();
  for (const [key, value] of userCache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      userCache.delete(key);
    }
  }
};

// Função para limpar cache de uma organização específica (usado na troca de org)
export const clearUserCacheForOrg = (orgId: string) => {
  userCache.delete(orgId);
  if (import.meta.env.DEV && devLastLoggedCacheHitOrgId === orgId) {
    devLastLoggedCacheHitOrgId = null;
  }
};

export function useCurrentUser() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { orgId, loading: orgLoading } = useCurrentOrgId();
  const lastFetchedOrgIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  
  useEffect(() => {
    let mounted = true;

    // Se ainda está carregando org, aguardar
    if (orgLoading) {
      return;
    }

    // Se não há orgId, limpar usuário
    if (!orgId) {
      if (import.meta.env.DEV) {
        devLastLoggedCacheHitOrgId = null;
      }
      setUser(null);
      setLoading(false);
      clearSentryUser();
      return;
    }

    // Evitar múltiplas chamadas simultâneas
    if (isFetchingRef.current) {
      return;
    }

    const fetchCurrentUser = async (): Promise<void> => {
      isFetchingRef.current = true;
      
      try {
        // Limpar cache expirado
        cleanExpiredCache();

        // Verificar cache primeiro
        const cacheKey = orgId;
        const cached = userCache.get(cacheKey);
        if (cached && cached.user) {
          const now = Date.now();
          if (now - cached.timestamp < CACHE_TTL) {
            devLogCachedUserOnce(cacheKey, cached.user.email);
            if (mounted) {
              setUser(prev => {
                if (prev && prev.user_id === cached.user.user_id && prev.role === cached.user.role) return prev;
                return cached.user;
              });
              setLoading(false);
              lastFetchedOrgIdRef.current = orgId;
            }
            isFetchingRef.current = false;
            return;
          }
        }

        if (import.meta.env.DEV) console.log('🟣 [useCurrentUser] Fetching user from API', { orgId });

        // Buscar sessão
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }

        let authUser = sessionData?.session?.user;

        if (!authUser) {
          // Se não há sessão, tentar getUser() como fallback
          const { data: authData, error: authError } = await supabase.auth.getUser();
          if (authError || !authData?.user) {
            if (mounted) {
              setUser(null);
              setLoading(false);
            }
            userCache.set(cacheKey, { user: null, timestamp: Date.now() });
            isFetchingRef.current = false;
            return;
          }
          authUser = authData.user;
        }

        // Buscar perfil do usuário
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', authUser.id)
          .maybeSingle();

        if (profileError) {
          throw profileError;
        }

        // Bloquear acesso se usuário estiver desativado pelo super-admin
        if (profile && (profile as { is_active?: boolean }).is_active === false) {
          await supabase.auth.signOut();
          if (mounted) {
            setUser(null);
            setLoading(false);
          }
          isFetchingRef.current = false;
          return;
        }

        // Buscar role na organização atual
        const { data: membership } = await supabase
          .from('memberships')
          // `can_manage_agenda` e `cargo` ficam FORA do select: a coluna foi removida de
          // `memberships` (13/09) quando o eixo virou a permissão `desempenho.gestao_agenda.*`.
          // Pedir coluna inexistente faz o PostgREST rejeitar a query TODA — era assim que
          // todo usuário virava role='rep'/isAdmin=false.
          .select('role')
          .eq('user_id', authUser.id)
          .eq('org_id', orgId)
          .eq('status', 'active')
          .maybeSingle();

        const mRole = (membership as { role?: string } | null)?.role;
        const userData: UserProfile = {
          user_id: authUser.id,
          email: profile?.email || authUser.email || null,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
          sonax_widget_script: profile?.sonax_widget_script || null,
          id_sonax: profile?.id_sonax || null,
          senha_sonax: profile?.senha_sonax || null,
          role: (mRole as UserProfile['role']) || 'rep',
          isAdmin: mRole === 'admin' || false,
          isOwner: mRole === 'owner' || false,
          created_at: profile?.created_at
        };

        if (import.meta.env.DEV) {
          console.log('🟣 [useCurrentUser] User loaded successfully', { email: userData.email, role: userData.role });
        }

        // Setar contexto Sentry
        setSentryUser({
          id: userData.user_id,
          email: userData.email,
          orgId,
          role: userData.role,
        });

        // Atualizar cache
        userCache.set(cacheKey, { 
          user: userData, 
          timestamp: Date.now() 
        });

        if (mounted) {
          setUser(prev => {
            if (prev && prev.user_id === userData.user_id && prev.role === userData.role) return prev;
            return userData;
          });
          setLoading(false);
          lastFetchedOrgIdRef.current = orgId;
        }
      } catch (err) {
        console.error('🟣 [useCurrentUser] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar dados do usuário');
          setLoading(false);
        }
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Executar fetch imediatamente
    fetchCurrentUser();

    return () => {
      mounted = false;
    };
   
  }, [orgId, orgLoading]);

  return { user, loading, error };
}
